var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsxs as _jsxs, jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import { Banner, Button, Checkbox, FlexBox, Modal, ModalBody, ModalFoot, ModalHead, Spinner, Text, } from '@dixa/lib-shared-habla';
import { client as elevioClient } from 'elevio';
import { useCallback, useState } from 'react';
import { useMount } from 'react-use';
import { queuesApi } from 'apis/queues';
import { searchApi } from 'apis/search';
import { analytics } from 'app/utils/segment';
import { AppLink } from 'components/appLink';
import { NotificationType, useNotifications } from 'hooks/useNotifications';
import { articles } from 'utils/elevio';
import { createAppModal } from 'utils/modal';
import styles from './queueArchiveModal.module.scss';
export const QueueArchiveModal = createAppModal({ type: 'settings:queueArchive' }, ({ isOpen, modal: { arg: { queue, goToArchivedQueues }, }, meta, hide, }) => {
    const notify = useNotifications();
    const [isConfirmed, setIsConfirmed] = useState(queue.is_archived);
    const [updateQueue, { isLoading: isUpdating }] = queuesApi.useUpdateQueueMutation();
    const [readConversations, { openConversationsCount, hasConversations, isLoadingConversations },] = searchApi.useLazyReadAdvancedSearchConversationsIdsBulkQuery({
        selectFromResult: ({ data, isFetching }) => {
            var _a;
            return ({
                isLoadingConversations: isFetching,
                openConversationsCount: data === null || data === void 0 ? void 0 : data.data.total_conversations,
                hasConversations: ((_a = data === null || data === void 0 ? void 0 : data.data.total_conversations) !== null && _a !== void 0 ? _a : 0) > 0,
            });
        },
    });
    useMount(() => {
        if (queue.is_archived)
            return;
        readConversations({
            type: 'conversations',
            params: {
                filters: {
                    strategy: 'all',
                    conditions: [
                        {
                            field: 'queue_id',
                            operator: 'is_one',
                            values: [{ label: queue.name, value: queue.id }],
                        },
                        {
                            field: 'status',
                            operator: 'is_one',
                            values: [
                                { label: '', value: 'open' },
                                { label: '', value: 'pending' },
                            ],
                        },
                    ],
                },
            },
        });
    });
    const handleConfirm = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        try {
            const nextIsArchived = !queue.is_archived;
            const { data: updatedQueue } = yield updateQueue({
                id: queue.id,
                queue: { is_archived: nextIsArchived },
            }).unwrap();
            if (nextIsArchived) {
                analytics.settingsQueuesArchiveQueueClicked({
                    openConversationsCount,
                });
            }
            else {
                analytics.settingsQueuesUnarchiveQueueClicked();
            }
            notify({
                title: queue.is_archived ? 'Queue unarchived' : 'Queue archived',
                text: `"${queue.name}" has been ${queue.is_archived ? 'unarchived' : 'archived'}.`,
                icon: 'i-done',
                time: 5000,
                leftAction: {
                    text: 'See all archived queues',
                    action: goToArchivedQueues,
                },
            }, NotificationType.Success);
            meta === null || meta === void 0 ? void 0 : meta.resolve({ updatedQueue });
            hide === null || hide === void 0 ? void 0 : hide();
        }
        catch (error) {
            notify({
                title: 'Something went wrong while updating the queue.',
            }, NotificationType.Error);
        }
    }), [
        goToArchivedQueues,
        hide,
        meta,
        notify,
        openConversationsCount,
        queue,
        updateQueue,
    ]);
    const renderArchiveContent = useCallback(() => {
        const conversationsLink = (_jsxs(AppLink, Object.assign({ path: "/conversations", params: {
                search: { queue: [queue.id], status: ['open', 'pending'] },
            }, target: "_blank" }, { children: [openConversationsCount, " open", ' ', openConversationsCount === 1 ? 'conversation' : 'conversations'] })));
        return (_jsxs(_Fragment, { children: [isLoadingConversations && (_jsxs(FlexBox, Object.assign({ flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "300" }, { children: [_jsx(Spinner, {}), _jsx(Text.Paragraph, { children: "Checking open conversations..." })] }))), !isLoadingConversations && (_jsxs(FlexBox, Object.assign({ gap: "400", flexDirection: "column" }, { children: [_jsx(Banner, { variant: "warning", description: _jsx(Text.Paragraph, { children: "Please unassign this queue from all flows and third-party integrations where this queue is referenced before archiving it." }), actionSlot: _jsx(Button, Object.assign({ variant: "secondary", onClick: () => {
                                    elevioClient.openArticle(articles.settings.queues.archiving);
                                } }, { children: "Read more" })), icon: null }), _jsx(Text.Paragraph, { children: "This queue will no longer be visible as an option in various selectors and dashboards." }), hasConversations && (_jsxs(Text.Paragraph, { children: ["Please be aware that this queue has ", conversationsLink, ". We suggest to close or transfer these conversations out of this queue before archiving."] })), _jsxs(FlexBox, Object.assign({ as: "label", alignItems: "center", gap: "300" }, { children: [_jsx(Checkbox, { name: "confirmQueueArchive", checked: isConfirmed, onChange: () => setIsConfirmed((isCheckboxChecked) => !isCheckboxChecked) }), _jsx(Text.Paragraph, { children: hasConversations
                                        ? 'I am aware that this queue has open active conversations that will remain in this queue and might still receive new conversations if it was not unassigned from its respective flows.'
                                        : 'I am aware that this queue might still receive new conversations if it was not unassigned from its respective flows.' })] }))] })))] }));
    }, [
        hasConversations,
        isConfirmed,
        isLoadingConversations,
        openConversationsCount,
        queue.id,
    ]);
    const renderUnarchiveContent = useCallback(() => {
        return (_jsx(Text.Paragraph, { children: "Are you sure you want to unarchive this queue?" }));
    }, []);
    return (_jsxs(Modal, Object.assign({ isOpen: isOpen, onClose: hide, className: styles.modal }, { children: [_jsx(ModalHead, { title: `${queue.is_archived ? 'Unarchive' : 'Archive'} "${queue.name}"`, onClose: hide }), _jsx(ModalBody, { children: queue.is_archived
                    ? renderUnarchiveContent()
                    : renderArchiveContent() }), _jsx(ModalFoot, { cancelOnClick: hide, confirmName: queue.is_archived ? 'Unarchive' : 'Archive', confirmOnClick: handleConfirm, confirmProps: {
                    type: 'submit',
                    isLoading: isUpdating,
                    disabled: !isConfirmed || isUpdating,
                } })] })));
});
